import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "./alert-queue";

// Per-checkpoint rollout readiness — public.management_checkpoint_rollout_status (one row per
// route-checkpoint; safe labels + readiness flags + blockers, no token hashes/coordinates/ids
// beyond what the view exposes). Explicit safe column select (no wildcard), matching the map's
// hardened pattern. Read-only.

export interface CheckpointRolloutRow {
  checkpointId: string;
  checkpointName: string;
  routeName: string | null;
  siteName: string | null;
  isRequired: boolean;
  operationalStatus: string;
  preferredMethod: string | null;
  usesQr: boolean;
  usesNfc: boolean;
  hasGeofence: boolean;
  activeQrTokens: number;
  activeNfcTokens: number;
  activeCheckpointMaterial: number;
  hasCheckpointMaterial: boolean;
  rolloutStatus: string;
  blockers: string[];
  lastScanAt: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asInt(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.trunc(value) : 0;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

export const CHECKPOINT_ROLLOUT_COLUMNS =
  "checkpoint_id, checkpoint_name, route_name, site_name, is_required, checkpoint_operational_status, preferred_verification_method, uses_qr_token, uses_nfc_token, has_geofence_config, active_qr_token_count, active_nfc_token_count, active_checkpoint_material_count, rollout_status, rollout_blockers, last_scan_at";

export function mapCheckpointRolloutRow(raw: Record<string, unknown>): CheckpointRolloutRow {
  return {
    checkpointId: asString(raw.checkpoint_id) ?? "",
    checkpointName: asString(raw.checkpoint_name) ?? "Checkpoint",
    routeName: asString(raw.route_name),
    siteName: asString(raw.site_name),
    isRequired: raw.is_required === true,
    operationalStatus: asString(raw.checkpoint_operational_status) ?? "active",
    preferredMethod: asString(raw.preferred_verification_method),
    usesQr: raw.uses_qr_token === true,
    usesNfc: raw.uses_nfc_token === true,
    hasGeofence: raw.has_geofence_config === true,
    activeQrTokens: asInt(raw.active_qr_token_count),
    activeNfcTokens: asInt(raw.active_nfc_token_count),
    activeCheckpointMaterial: asInt(raw.active_checkpoint_material_count),
    hasCheckpointMaterial: asInt(raw.active_checkpoint_material_count) > 0,
    rolloutStatus: asString(raw.rollout_status) ?? "needs_setup",
    blockers: asStringArray(raw.rollout_blockers),
    lastScanAt: asString(raw.last_scan_at),
  };
}

const BLOCKER_ACTIONS: Record<string, string> = {
  missing_checkpoint_material: "Create checkpoint material",
  missing_qr_token: "Mint a QR token",
  missing_nfc_token: "Mint an NFC token",
  missing_geofence_config: "Add geofence coordinates",
};

/** The single clearest next setup step for a checkpoint (operational status wins, then blockers). */
export function nextSetupAction(
  row: Pick<CheckpointRolloutRow, "rolloutStatus" | "blockers" | "operationalStatus">,
): string {
  if (row.operationalStatus === "maintenance") return "In maintenance";
  if (row.operationalStatus === "disabled") return "Disabled";
  if (row.blockers.length === 0) {
    return row.rolloutStatus === "ready" ? "Ready" : "—";
  }
  for (const blocker of row.blockers) {
    const action = BLOCKER_ACTIONS[blocker];
    if (action !== undefined) return action;
  }
  return humanizeLabel(row.blockers[0] ?? "");
}

const CHECKPOINT_ROLLOUT_LIMIT = 2000;

/** Fetch per-checkpoint rollout readiness. Explicit safe columns only. Throws on read error. */
export async function fetchCheckpointRollout(
  client: SupabaseClient,
  accountId: string,
): Promise<CheckpointRolloutRow[]> {
  const { data, error } = await client
    .from("management_checkpoint_rollout_status")
    .select(CHECKPOINT_ROLLOUT_COLUMNS)
    .eq("account_id", accountId)
    .limit(CHECKPOINT_ROLLOUT_LIMIT);
  if (error !== null) {
    throw new Error("Could not load checkpoint readiness.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapCheckpointRolloutRow(row as Record<string, unknown>));
}
